import {
  AircraftClass,
  type AircraftClassId,
  CONTRACT_INITIAL_SCORE,
  INITIAL_RATING,
} from './airlines';
import { BinaryReader, BinaryWriter } from './binary';
import { BuildingType, type BuildingTypeId } from './buildings';
import { SIM_VERSION } from './constants';
import type { DailyGoal, DailyGoalKindId } from './daily';
import { FEE_DEFAULT, type Loan } from './finance';
import { Ground } from './ground';
import { GSE_COUNT } from './handling';
import {
  type Aircraft,
  type Contract,
  emptyLedger,
  emptyReport,
  type Flight,
  type FlightStatusId,
  type HoldReasonId,
  type Ledger,
  type Offer,
  type PhaseId,
  REPORT_FIELDS,
  type RunwayClosure,
  SERVICE_COUNT,
  showedUp,
  type TransferGroup,
} from './operations';
import { ClosureKind } from './pavement';
import { RATING_CATEGORIES } from './reputation';
import { Rng } from './rng';
import { type RunwaySnow, SNOW_WAIT, type SnowJob } from './snow';
import { ALL_AUTO, autoStaff } from './staff';
import { mapTheme } from './theme';
import { INITIAL_CLIMATE, INITIAL_SKY, INITIAL_VISIBILITY, type SkyId } from './weather';
import { INITIAL_WIND_DIR, INITIAL_WIND_SPEED } from './wind';
import { World } from './world';

/**
 * Canonical binary world snapshot. The same bytes serve save files, co-op join, desync
 * recovery and replay start states, so the same state must always produce the same bytes.
 *
 * Layout: magic u32 · formatVersion u16 · simVersion string · sections[id u16, length u32, body]
 * Readers skip unknown sections. When the format changes, bump SNAPSHOT_FORMAT_VERSION and add a
 * migration step from the previous version; never edit an old one.
 *
 * History:
 *  - v1: wall-building prototype (M0); not loadable, never released.
 *  - v2: prefab buildings (M0.5).
 *  - v3: adds the Ops section (airlines, flights, aircraft, ledger). v2 saves load with no
 *        operations, which is exactly their state.
 *  - v4: passengers and ground services (flight loads, queues, turnaround services, rating,
 *        itemised ledger). v3 flights load with no passengers and finished services.
 *  - v5: traffic (M1.5): runway mode and direction, wind, aircraft runway direction, contract
 *        score, renewal offers, punctuality in the ledger. v4 runways load as mixed-use in
 *        their first direction, contracts at the initial score.
 *  - v6: ground handling (M3): vehicles held per aircraft, contractor flag, delay diagnosis per
 *        flight and per day, handling fees. v5 aircraft load holding no vehicles (their
 *        services carry on), with no lost minutes recorded.
 *  - v7: weather and maintenance (M3.5): sky, visibility and climate; de-icing and technical
 *        repair per aircraft; technical delay and maintenance income. v6 worlds load under a
 *        clear sky with no repairs or de-icing pending.
 *  - v8: the motorway (a network kind) and the state incentive grant. v7 worlds get the motorway
 *        laid on load wherever its tiles are still free; the grant has not been offered yet.
 *  - v9: economy and staff (M4): staff per role, loans, fee settings, wages and instalments in
 *        the ledger. v8 worlds load with every team on automatic staffing (hired to what the
 *        buildings need), no loans and the standard tariff.
 *  - v10: the airport's name; rating categories and online buzz; slots planned per offer, airline strikes per contract; ground vehicles driving to the aircraft (time to arrival per vehicle
 *         and for the contractor's crew). v9 worlds load unnamed, with every vehicle already
 *         at its aircraft.
 *  - v11: emergencies: the kind declared per aircraft, runways closed after one. v10 worlds load
 *         with no emergency under way.
 *  - v12: milestones reached, departures per aircraft class, rewards in the ledger. Older worlds
 *         load with none reached: the ones already true pay out on the first minute.
 *  - v13: air freight: cargo offers, contracts and flights (tonnes), freight in the ledger.
 *         Older worlds load with passenger flights only.
 *  - v14: night shifts (percent of each team on duty at night). Older worlds load with full
 *         night shifts.
 *  - v15: inbound delays: minutes each flight arrives late from its origin, and their daily
 *         total. Older worlds load with every flight on time.
 *  - v16: night noise quota: points used tonight, noise fines in the ledger. Older worlds load
 *         with tonight's quota untouched.
 *  - v17: hotel revenue and guests in the ledger (the hotel is a new building type).
 *  - v18: transfer passengers: per flight in and out, the airside pool, daily totals. Older
 *         worlds load with no one connecting.
 *  - v19: snow on the runways and their closure for clearing. Older worlds load with clear
 *         runways.
 *  - v20: overdraft interest in the ledger.
 *  - v21: terminal facilities (desks, lanes, lounge, shops) per building.
 *  - v22: charter events on offers and contracts.
 *  - v23: flight kitchen revenue in the ledger.
 *  - v24: the charter event kept on each flight.
 *  - v25: marketing: campaign timers, campaign spending in the ledger.
 *  - v26: minutes lost to lightning alerts, per flight and per day.
 *  - v27: bags lost in the ledger.
 *  - v28: pay policy (wage percent) and a strike under way.
 *  - v29: runway wear; runway closures for works, planned ahead (start tick and kind). Older
 *         worlds load with runways as new and every closure an emergency one.
 *  - v30: the sky forecast for the coming hours. Older worlds make one on the next hour.
 *  - v31: the market: oil price and economy. Older worlds load with both at normal.
 *  - v32: market flags (an oil shock, a recession or a boom under way). Older worlds load with
 *         none; it is noticed on the next day.
 *  - v33: departures counted for the light class (private jets, class id 4) too. Older worlds
 *         load with none.
 *  - v34: the military zone: runway usage (joint, civil, military), departures of the military
 *         classes (5–7), the ministry's payments in the ledger. Older worlds load with every
 *         runway joint and nothing military.
 *  - v35: passenger stages: departing passengers through check-in and through security per
 *         flight. Older worlds load with everyone served so far through every stage.
 *  - v36: aircraft parking: a flight's stay (turnaround, night stop, parking rental), rental
 *         days on offers and contracts, parking charges in the ledger. Older worlds load with
 *         every flight a turnaround, no rentals and no charges.
 *  - v37: passengers come by road: a flight's passengers who have reached the terminal. Older
 *         worlds load with everyone who had set off by then there (no one stuck on the road).
 *  - v38: the motorway on a viaduct and its interchange (layout unchanged): older worlds whose
 *         roads already meet the motorway get the link road to it on load.
 *  - v39: terminal facilities take 16 bits (the metro). Older worlds load without one.
 *  - v40: snow per runway, plough teams out, how hard it snows and the winter's blizzard. Older
 *         worlds give every runway the snow they had, a snow closure each if theirs were shut,
 *         and the snowfall is rolled on the next hour.
 *  - v41: admin mode (on, ever used). Older worlds load with it never used.
 *  - v42: departures of the helicopter class (8). Older worlds load with none.
 *  - v43: today's goals. Older worlds get theirs on the first minute.
 *  - v44: a VIP aboard a flight (vip.ts). Older flights carry none.
 *  - v45: VIPs pleased and air shows flown, ever. Older worlds start counting at none.
 *  - v46: the airlines that have flown from here (the album). Older worlds start with those of
 *         their contracts that have flown.
 *  - v47: steps of the sound insulation programme paid. Older worlds have paid none. (Toll plazas,
 *         added with it, live in the network's own bits: no format of their own.)
 *  - v48: tolls paid at the plazas, a line of the day's accounts. Older days took none.
 *  - v49: runways, stands and parking positions in three sizes (new building types, which need
 *         no format of their own) and large ones kept for the big aircraft (bigOnly). Older
 *         buildings are kept for no one.
 *  - v50: each contract's record (what its flights went through, for the airline's view), and
 *         whether widebodies may still use medium stands (an older save with widebody contracts
 *         and no large stand). Older contracts start with an empty record. The all-time
 *         ledger (every finished day added up); an older save starts it with its departures.
 *  - v51: the airport city's figures in the ledger (sales, customers, office rent). The city's
 *         buildings are new types and need no format of their own.
 *  - v52: the map's theme (World.theme: the green countryside, the desert, the mountains).
 *         Older saves are on the green countryside.
 */
const MAGIC = 0x37324d41; // "AM27"
export const SNAPSHOT_FORMAT_VERSION = 52;
/** Entries of the departures-per-class counter (index = aircraft class id). */
const DEPARTURE_CLASSES = 9;

const Section = { Meta: 1, Ground: 2, Buildings: 3, Ops: 4 } as const;

export function writeSnapshot(world: World): Uint8Array<ArrayBuffer> {
  const w = new BinaryWriter(1 << 16);
  w.u32(MAGIC).u16(SNAPSHOT_FORMAT_VERSION).string(SIM_VERSION);

  writeSection(w, Section.Meta, (s) => {
    s.u32(world.seq).u32(world.time).u32(world.seed).f64(world.money);
    for (const word of world.rng.state) s.u32(word);
    s.u32(world.nextBuildingId);
  });

  writeSection(w, Section.Ground, (s) => {
    const g = world.ground;
    s.u16(g.width).u16(g.height).raw(g.owned).raw(g.network);
  });

  writeSection(w, Section.Buildings, (s) => {
    s.u32(world.buildings.length);
    for (const b of world.buildings) {
      s.u32(b.id).u8(b.type).u16(b.x).u16(b.y).u8(b.rot).u8(b.level);
      s.u32(b.buildRemaining).u32(b.buildTotal).f64(b.spent).u8(b.mode).u8(b.flow);
      s.u16(b.facilities).u16(b.wear).u8(b.usage).u8(b.bigOnly);
    }
  });

  writeSection(w, Section.Ops, (s) => {
    s.u32(world.nextOpsId).u32(world.rating).u8(world.windDir).u8(world.windSpeed);
    s.u8(world.sky).u8(world.visibility).i32(world.climate);
    s.u8(world.grantState).f64(world.grantAmount).u32(world.grantDeadline);
    s.u8(world.staffAuto);
    for (let i = 0; i < 4; i++) s.u16(world.staffHired[i] ?? 0);
    s.u8(world.landingFeePct).u8(world.paxFeePct).u8(world.loans.length);
    for (const l of world.loans) s.u32(l.id).f64(l.principal).f64(l.remaining).f64(l.payment);
    s.string(world.airportName);
    for (let i = 0; i < RATING_CATEGORIES; i++) s.u16(world.ratings[i] ?? 0);
    s.i32(world.buzz);
    s.u8(world.runwayClosures.length);
    for (const c of world.runwayClosures) s.u32(c.runwayId).u32(c.until).u32(c.from).u8(c.kind);
    s.u32(world.milestones);
    for (let i = 0; i < DEPARTURE_CLASSES; i++) s.u32(world.departuresByClass[i] ?? 0);
    for (let i = 0; i < 4; i++) s.u8(world.staffNight[i] ?? 100);
    s.u16(world.nightNoise);
    s.u16(world.transferPool.length);
    for (const g of world.transferPool) s.u32(g.at).u16(g.n);
    s.u8(world.snowfall).u32(world.blizzardAt).u32(world.blizzardUntil);
    s.u8(world.runwaySnow.length);
    for (const snow of world.runwaySnow) s.u32(snow.runwayId).u8(snow.depth);
    s.u8(world.snowJobs.length);
    for (const j of world.snowJobs) {
      s.u32(j.runwayId).u32(j.baseId).u8(j.team).u32(j.from).u32(j.arrive).u32(j.turn);
      s.u32(j.done).u32(j.home).u8(j.end);
    }
    s.u32(world.socialReadyAt).u32(world.promoUntil);
    s.u8(world.wagePct).u32(world.strikeUntil);
    s.u8(world.skyAhead.length);
    for (const sky of world.skyAhead) s.u8(sky);
    s.u16(world.oilIndex).i32(world.economy).u8(world.marketFlags);
    s.u8(world.adminMode);
    s.u8(world.dailyGoals.length);
    for (const g of world.dailyGoals) s.u8(g.kind).f64(g.target).f64(g.reward).u8(g.state);
    s.u32(world.vipsPleased).u32(world.airshows);
    s.u8(world.airlinesSeen.length);
    for (const id of world.airlinesSeen) s.u8(id);
    s.u8(world.noiseInsulation);
    s.u8(world.legacyWideStands ? 1 : 0);
    s.u8(world.theme);
    writeLedger(s, world.today);
    writeLedger(s, world.yesterday);
    writeLedger(s, world.allTime);
    s.u32(world.offers.length);
    for (const o of world.offers) {
      s.u32(o.id).u8(o.airline).u8(o.cls).u8(o.flightsPerDay).u8(o.slotOffset).u8(o.days);
      s.u32(o.expiresAt).u8(o.renewal).u8(o.slots.length);
      for (const slot of o.slots) s.u16(slot);
      s.u8(o.cargo).u8(o.event).u8(o.rental);
    }
    s.u32(world.contracts.length);
    for (const c of world.contracts) {
      s.u32(c.id).u8(c.airline).u8(c.cls).u32(c.endsAt).u32(c.served).u32(c.cancelled);
      s.f64(c.revenue).u8(c.score).u8(c.slots.length);
      for (const slot of c.slots) s.u16(slot);
      s.u8(c.strikes).u8(c.cargo).u8(c.event).u8(c.rental);
      for (const k of REPORT_FIELDS) s.u32(c.report[k]);
    }
    s.u32(world.flights.length);
    for (const f of world.flights) {
      s.u32(f.id).u32(f.contractId).u8(f.airline).u8(f.cls).u32(f.scheduledAt);
      s.u8(f.status).u8(f.holdReason).u32(f.aircraftId).u32(f.standId).f64(f.revenue);
      s.u32(f.finishedAt).u32(f.departAt).u16(f.paxIn).u16(f.paxOut).u16(f.paxReady);
      s.u16(f.paxBoarded).u32(f.waitSum).u32(f.offBlockAt).u8(f.gateClosed).u8(f.satisfaction);
      s.u16(f.lostArrival).u16(f.lostVehicles).u16(f.lostPassengers).u16(f.lostDeparture);
      s.u16(f.lostTechnical).u16(f.cargo).u16(f.lateBy).u16(f.transferIn).u16(f.transferOut);
      s.u8(f.event).u16(f.lostWeather).u16(f.paxCheckedIn).u16(f.paxScreened).u8(f.park);
      s.u16(f.paxArrived).u8(f.vip);
    }
    s.u32(world.aircraft.length);
    for (const a of world.aircraft) {
      s.u32(a.id).u32(a.flightId).u8(a.cls).u8(a.airline).u8(a.phase).u32(a.runwayId);
      s.u8(a.runwayDir);
      s.u8(a.runwayIndex).u32(a.runwayPos).u32(a.speed).u32(a.pathPos).u32(a.standId);
      s.u32(a.timer).u32(a.timerTotal).u32(a.path.length);
      for (const t of a.path) s.u32(t);
      for (let i = 0; i < SERVICE_COUNT; i++) s.u32(a.svcLeft[i] ?? 0).u32(a.svcTotal[i] ?? 0);
      s.u8(a.boardState).u32(a.boardMilli).u8(a.fuelFromDepot).u8(a.gse).u8(a.contracted);
      s.u32(a.deiceLeft).u32(a.deiceTotal).u32(a.techLeft).u32(a.techTotal);
      s.u32(a.crewEta);
      for (let k = 0; k < GSE_COUNT; k++) s.u32(a.gseEta[k] ?? 0);
      s.u8(a.emergency);
    }
  });

  return w.finish();
}

export interface SnapshotInfo {
  formatVersion: number;
  simVersion: string;
  /** Next bundle the world expects, game ticks elapsed and money (from the Meta section). */
  seq: number;
  time: number;
  money: number;
}

/**
 * Header facts of a snapshot without loading it: save lists show them, and a Room started from
 * a save continues numbering bundles at `seq`. Throws if the bytes are not a snapshot.
 */
export function readSnapshotInfo(bytes: Uint8Array): SnapshotInfo {
  const r = new BinaryReader(bytes);
  if (r.u32() !== MAGIC) throw new Error('Not an Airport Manager snapshot');
  const formatVersion = r.u16();
  const simVersion = r.string();
  let meta = { seq: 0, time: 0, money: 0 };
  while (r.remaining > 0) {
    const id = r.u16();
    const body = new BinaryReader(r.raw(r.u32()));
    if (id === Section.Meta) {
      const seq = body.u32();
      const time = body.u32();
      body.u32(); // seed
      meta = { seq, time, money: body.f64() };
      break;
    }
  }
  return { formatVersion, simVersion, ...meta };
}

export function readSnapshot(bytes: Uint8Array): World {
  const r = new BinaryReader(bytes);
  if (r.u32() !== MAGIC) throw new Error('Not an Airport Manager snapshot');
  const formatVersion = r.u16();
  if (formatVersion > SNAPSHOT_FORMAT_VERSION) {
    throw new Error(`Snapshot format ${formatVersion} is newer than this build supports`);
  }
  if (formatVersion < 2) {
    throw new Error(
      `Snapshot format ${formatVersion} is from the old prototype and cannot be loaded`,
    );
  }
  r.string(); // simVersion: informational for saves; co-op checks it during the handshake.

  let meta: {
    seq: number;
    time: number;
    seed: number;
    money: number;
    rng: number[];
    nextBuildingId: number;
  } | null = null;
  let ground: Ground | null = null;
  const buildings: World['buildings'] = [];
  let ops: {
    nextOpsId: number;
    rating: number;
    windDir: number;
    windSpeed: number;
    sky: SkyId;
    visibility: number;
    climate: number;
    grantState: number;
    grantAmount: number;
    grantDeadline: number;
    staffAuto: number;
    staffHired: number[];
    landingFeePct: number;
    paxFeePct: number;
    loans: Loan[];
    airportName: string;
    ratings: number[] | null;
    buzz: number;
    runwayClosures: RunwayClosure[];
    milestones: number;
    departuresByClass: number[];
    staffNight: number[];
    nightNoise: number;
    transferPool: TransferGroup[];
    snowfall: number;
    blizzardAt: number;
    blizzardUntil: number;
    runwaySnow: RunwaySnow[];
    snowJobs: SnowJob[];
    legacySnow: number;
    legacyClosed: number;
    socialReadyAt: number;
    promoUntil: number;
    wagePct: number;
    strikeUntil: number;
    skyAhead: number[];
    oilIndex: number;
    economy: number;
    marketFlags: number;
    adminMode: number;
    dailyGoals: DailyGoal[];
    vipsPleased: number;
    airshows: number;
    airlinesSeen: number[];
    noiseInsulation: number;
    legacyWideStands: boolean | null;
    theme: number;
    today: Ledger;
    yesterday: Ledger;
    allTime: Ledger | null;
    offers: Offer[];
    contracts: Contract[];
    flights: Flight[];
    aircraft: Aircraft[];
  } | null = null;

  while (r.remaining > 0) {
    const id = r.u16();
    const body = new BinaryReader(r.raw(r.u32()));
    switch (id) {
      case Section.Meta:
        meta = {
          seq: body.u32(),
          time: body.u32(),
          seed: body.u32(),
          money: body.f64(),
          rng: [body.u32(), body.u32(), body.u32(), body.u32()],
          nextBuildingId: body.u32(),
        };
        break;
      case Section.Ground: {
        ground = new Ground(body.u16(), body.u16());
        ground.owned.set(body.raw(ground.owned.length));
        ground.network.set(body.raw(ground.network.length));
        break;
      }
      case Section.Buildings: {
        const count = body.u32();
        for (let i = 0; i < count; i++) {
          buildings.push({
            id: body.u32(),
            type: body.u8() as BuildingTypeId,
            x: body.u16(),
            y: body.u16(),
            rot: body.u8(),
            level: body.u8(),
            buildRemaining: body.u32(),
            buildTotal: body.u32(),
            spent: body.f64(),
            mode: formatVersion >= 5 ? body.u8() : 0,
            flow: formatVersion >= 5 ? body.u8() : 0,
            facilities: formatVersion >= 39 ? body.u16() : formatVersion >= 21 ? body.u8() : 0,
            wear: formatVersion >= 29 ? body.u16() : 0,
            usage: formatVersion >= 34 ? body.u8() : 0,
            bigOnly: formatVersion >= 49 ? body.u8() : 0,
          });
        }
        break;
      }
      case Section.Ops:
        ops = readOps(body, formatVersion);
        break;
      default:
        break; // Unknown section from a newer minor version: ignore.
    }
  }

  if (!meta || !ground) throw new Error('Snapshot is missing required sections');
  const rng = new Rng(0);
  rng.state.set(meta.rng);
  const world = new World(meta.seed, ground, rng, meta.money);
  world.seq = meta.seq;
  world.time = meta.time;
  world.nextBuildingId = meta.nextBuildingId;
  world.buildings.push(...buildings);
  world.rebuildOccupancy();
  // Saves from before the motorway existed get it now, on whatever tiles are still free.
  if (formatVersion < 8) ground.layHighway();
  // Saves from before the viaduct get its interchange where their road meets the motorway.
  if (formatVersion < 38) ground.layInterchange();
  if (ops) {
    world.nextOpsId = ops.nextOpsId;
    world.rating = ops.rating;
    world.windDir = ops.windDir;
    world.windSpeed = ops.windSpeed;
    world.sky = ops.sky;
    world.visibility = ops.visibility;
    world.climate = ops.climate;
    world.grantState = ops.grantState;
    world.grantAmount = ops.grantAmount;
    world.grantDeadline = ops.grantDeadline;
    world.staffAuto = ops.staffAuto;
    world.staffHired = ops.staffHired;
    world.landingFeePct = ops.landingFeePct;
    world.paxFeePct = ops.paxFeePct;
    world.loans.push(...ops.loans);
    world.airportName = ops.airportName;
    // Older saves had one rating: every category starts from it.
    world.ratings = ops.ratings ?? new Array<number>(RATING_CATEGORIES).fill(ops.rating);
    world.buzz = ops.buzz;
    world.runwayClosures.push(...ops.runwayClosures);
    world.milestones = ops.milestones;
    for (let i = 0; i < DEPARTURE_CLASSES; i++) {
      world.departuresByClass[i] = ops.departuresByClass[i] ?? 0;
    }
    world.staffNight = ops.staffNight;
    world.nightNoise = ops.nightNoise;
    world.transferPool.push(...ops.transferPool);
    world.snowfall = ops.snowfall;
    world.blizzardAt = ops.blizzardAt;
    world.blizzardUntil = ops.blizzardUntil;
    world.runwaySnow.push(...ops.runwaySnow);
    world.snowJobs.push(...ops.snowJobs);
    // Older worlds had one depth for all the runways, all of them shut at once.
    if (ops.legacySnow > 0 || ops.legacyClosed) {
      for (const b of world.buildings) {
        if (b.type !== BuildingType.Runway || b.level < 1) continue;
        if (ops.legacySnow > 0) world.runwaySnow.push({ runwayId: b.id, depth: ops.legacySnow });
        if (!ops.legacyClosed) continue;
        world.runwayClosures.push({
          runwayId: b.id,
          from: world.time,
          until: world.time + SNOW_WAIT,
          kind: ClosureKind.Snow,
        });
      }
    }
    world.socialReadyAt = ops.socialReadyAt;
    world.promoUntil = ops.promoUntil;
    world.wagePct = ops.wagePct;
    world.strikeUntil = ops.strikeUntil;
    world.skyAhead.push(...ops.skyAhead);
    world.oilIndex = ops.oilIndex;
    world.economy = ops.economy;
    world.marketFlags = ops.marketFlags;
    world.adminMode = ops.adminMode;
    world.dailyGoals.push(...ops.dailyGoals);
    world.vipsPleased = ops.vipsPleased;
    world.airshows = ops.airshows;
    world.airlinesSeen.push(...ops.airlinesSeen);
    world.noiseInsulation = ops.noiseInsulation;
    // Before v50 widebodies used level 2 medium stands: an airport flying them with no large
    // stand keeps doing so until it builds one.
    world.legacyWideStands =
      ops.legacyWideStands ??
      (world.contracts.some((c) => c.cls === AircraftClass.Widebody) &&
        !world.buildings.some((b) => b.type === BuildingType.LargeStand && b.buildRemaining === 0));
    world.theme = mapTheme(ops.theme);
    world.today = ops.today;
    world.yesterday = ops.yesterday;
    if (ops.allTime) world.allTime = ops.allTime;
    else {
      // Before v50 only the departures were counted all along (the finished days' share).
      let departures = 0;
      for (const n of ops.departuresByClass) departures += n;
      world.allTime = emptyLedger(0);
      world.allTime.departures = Math.max(0, departures - ops.today.departures);
    }
    world.offers.push(...ops.offers);
    world.contracts.push(...ops.contracts);
    world.flights.push(...ops.flights);
    for (const f of world.flights) {
      if (f.paxArrived < 0) f.paxArrived = Math.max(f.paxCheckedIn, showedUp(f, world.time));
    }
    world.aircraft.push(...ops.aircraft);
  }
  // Saves from before staff existed: every team hired to what its buildings need.
  if (formatVersion < 9) autoStaff(world);
  ground.markAllDirty();
  return world;
}

function writeSection(w: BinaryWriter, id: number, body: (w: BinaryWriter) => void): void {
  w.u16(id);
  const lengthAt = w.length;
  w.u32(0);
  const start = w.length;
  body(w);
  w.patchU32(lengthAt, w.length - start);
}

function writeLedger(w: BinaryWriter, l: Ledger): void {
  w.u32(l.day).f64(l.landing).f64(l.passenger).f64(l.retail).f64(l.fuel).f64(l.parking);
  w.f64(l.upkeep).f64(l.penalties).u32(l.departures).u32(l.passengers).u32(l.missed);
  w.u32(l.onTime).u32(l.delayMinutes).u32(l.cancelled);
  w.f64(l.handling).u32(l.lostArrival).u32(l.lostVehicles).u32(l.lostPassengers);
  w.u32(l.lostDeparture).u32(l.lostTechnical).f64(l.maintenance);
  w.f64(l.wages).f64(l.loans).f64(l.transit).f64(l.rewards).f64(l.cargo).u32(l.tonnes);
  w.u32(l.lostInbound).f64(l.noiseFines);
  w.f64(l.hotel).u32(l.hotelGuests);
  w.u32(l.transfers).u32(l.missedConnections);
  w.f64(l.overdraft).f64(l.catering).f64(l.marketing).u32(l.lostWeather).u32(l.bagsLost);
  w.f64(l.military).f64(l.aircraftParking).f64(l.tolls);
  w.f64(l.commerce).u32(l.customers).f64(l.rent);
}

function readLedger(r: BinaryReader, version: number): Ledger {
  if (version < 4) {
    // v3 kept a single flight-income total: book it all as landing fees.
    const l = emptyLedger(r.u32());
    l.landing = r.f64();
    l.upkeep = r.f64();
    l.penalties = r.f64();
    return l;
  }
  const l: Ledger = {
    day: r.u32(),
    landing: r.f64(),
    passenger: r.f64(),
    retail: r.f64(),
    fuel: r.f64(),
    parking: r.f64(),
    upkeep: r.f64(),
    penalties: r.f64(),
    departures: r.u32(),
    passengers: r.u32(),
    missed: r.u32(),
    onTime: 0,
    delayMinutes: 0,
    cancelled: 0,
    handling: 0,
    lostArrival: 0,
    lostVehicles: 0,
    lostPassengers: 0,
    lostDeparture: 0,
    lostTechnical: 0,
    lostInbound: 0,
    noiseFines: 0,
    hotel: 0,
    hotelGuests: 0,
    transfers: 0,
    missedConnections: 0,
    overdraft: 0,
    catering: 0,
    marketing: 0,
    lostWeather: 0,
    bagsLost: 0,
    military: 0,
    aircraftParking: 0,
    tolls: 0,
    maintenance: 0,
    wages: 0,
    loans: 0,
    transit: 0,
    rewards: 0,
    cargo: 0,
    tonnes: 0,
    commerce: 0,
    customers: 0,
    rent: 0,
  };
  if (version >= 5) {
    l.onTime = r.u32();
    l.delayMinutes = r.u32();
    l.cancelled = r.u32();
  }
  if (version >= 6) {
    l.handling = r.f64();
    l.lostArrival = r.u32();
    l.lostVehicles = r.u32();
    l.lostPassengers = r.u32();
    l.lostDeparture = r.u32();
  }
  if (version >= 7) {
    l.lostTechnical = r.u32();
    l.maintenance = r.f64();
  }
  if (version >= 9) {
    l.wages = r.f64();
    l.loans = r.f64();
  }
  if (version >= 10) l.transit = r.f64();
  if (version >= 12) l.rewards = r.f64();
  if (version >= 13) {
    l.cargo = r.f64();
    l.tonnes = r.u32();
  }
  if (version >= 15) l.lostInbound = r.u32();
  if (version >= 16) l.noiseFines = r.f64();
  if (version >= 17) {
    l.hotel = r.f64();
    l.hotelGuests = r.u32();
  }
  if (version >= 18) {
    l.transfers = r.u32();
    l.missedConnections = r.u32();
  }
  if (version >= 20) l.overdraft = r.f64();
  if (version >= 23) l.catering = r.f64();
  if (version >= 25) l.marketing = r.f64();
  if (version >= 26) l.lostWeather = r.u32();
  if (version >= 27) l.bagsLost = r.u32();
  if (version >= 34) l.military = r.f64();
  if (version >= 36) l.aircraftParking = r.f64();
  if (version >= 48) l.tolls = r.f64();
  if (version >= 51) {
    l.commerce = r.f64();
    l.customers = r.u32();
    l.rent = r.f64();
  }
  return l;
}

function readOps(r: BinaryReader, version: number) {
  const nextOpsId = r.u32();
  const rating = version >= 4 ? r.u32() : INITIAL_RATING;
  const windDir = version >= 5 ? r.u8() : INITIAL_WIND_DIR;
  const windSpeed = version >= 5 ? r.u8() : INITIAL_WIND_SPEED;
  const sky = (version >= 7 ? r.u8() : INITIAL_SKY) as SkyId;
  const visibility = version >= 7 ? r.u8() : INITIAL_VISIBILITY;
  const climate = version >= 7 ? r.i32() : INITIAL_CLIMATE;
  const grantState = version >= 8 ? r.u8() : 0;
  const grantAmount = version >= 8 ? r.f64() : 0;
  const grantDeadline = version >= 8 ? r.u32() : 0;
  let staffAuto = ALL_AUTO;
  const staffHired = [0, 0, 0, 0];
  let landingFeePct = FEE_DEFAULT;
  let paxFeePct = FEE_DEFAULT;
  const loans: Loan[] = [];
  if (version >= 9) {
    staffAuto = r.u8();
    for (let i = 0; i < 4; i++) staffHired[i] = r.u16();
    landingFeePct = r.u8();
    paxFeePct = r.u8();
    for (let n = r.u8(); n > 0; n--) {
      loans.push({ id: r.u32(), principal: r.f64(), remaining: r.f64(), payment: r.f64() });
    }
  }
  const airportName = version >= 10 ? r.string() : '';
  let ratings: number[] | null = null;
  let buzz = 0;
  if (version >= 10) {
    ratings = [];
    for (let i = 0; i < RATING_CATEGORIES; i++) ratings.push(r.u16());
    buzz = r.i32();
  }
  const runwayClosures: RunwayClosure[] = [];
  if (version >= 11) {
    for (let n = r.u8(); n > 0; n--) {
      const runwayId = r.u32();
      const until = r.u32();
      const from = version >= 29 ? r.u32() : 0;
      const kind = version >= 29 ? r.u8() : 0;
      runwayClosures.push({ runwayId, from, until, kind });
    }
  }
  let milestones = 0;
  const departuresByClass = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  if (version >= 12) {
    milestones = r.u32();
    // Format 33 added the light class (id 4), 34 the military classes (5–7), 42 helicopters (8).
    const classes = version >= 42 ? DEPARTURE_CLASSES : version >= 34 ? 8 : version >= 33 ? 5 : 4;
    for (let i = 0; i < classes; i++) departuresByClass[i] = r.u32();
  }
  const staffNight = [100, 100, 100, 100];
  if (version >= 14) for (let i = 0; i < 4; i++) staffNight[i] = r.u8();
  const nightNoise = version >= 16 ? r.u16() : 0;
  const transferPool: TransferGroup[] = [];
  if (version >= 18)
    for (let n = r.u16(); n > 0; n--) transferPool.push({ at: r.u32(), n: r.u16() });
  // Format 40: snow per runway and plough teams; before, one depth and one closed flag for all.
  let snowfall = 0;
  let blizzardAt = 0;
  let blizzardUntil = 0;
  const runwaySnow: RunwaySnow[] = [];
  const snowJobs: SnowJob[] = [];
  let legacySnow = 0;
  let legacyClosed = 0;
  if (version >= 40) {
    snowfall = r.u8();
    blizzardAt = r.u32();
    blizzardUntil = r.u32();
    for (let n = r.u8(); n > 0; n--) runwaySnow.push({ runwayId: r.u32(), depth: r.u8() });
    for (let n = r.u8(); n > 0; n--) {
      snowJobs.push({
        runwayId: r.u32(),
        baseId: r.u32(),
        team: r.u8(),
        from: r.u32(),
        arrive: r.u32(),
        turn: r.u32(),
        done: r.u32(),
        home: r.u32(),
        end: r.u8(),
      });
    }
  } else if (version >= 19) {
    legacySnow = r.u8();
    legacyClosed = r.u8();
  }
  const socialReadyAt = version >= 25 ? r.u32() : 0;
  const promoUntil = version >= 25 ? r.u32() : 0;
  const wagePct = version >= 28 ? r.u8() : 100;
  const strikeUntil = version >= 28 ? r.u32() : 0;
  const skyAhead: number[] = [];
  if (version >= 30) for (let n = r.u8(); n > 0; n--) skyAhead.push(r.u8());
  const oilIndex = version >= 31 ? r.u16() : 100;
  const economy = version >= 31 ? r.i32() : 0;
  const marketFlags = version >= 32 ? r.u8() : 0;
  const adminMode = version >= 41 ? r.u8() : 0;
  const dailyGoals: DailyGoal[] = [];
  if (version >= 43) {
    for (let n = r.u8(); n > 0; n--) {
      dailyGoals.push({
        kind: r.u8() as DailyGoalKindId,
        target: r.f64(),
        reward: r.f64(),
        state: r.u8(),
      });
    }
  }
  const vipsPleased = version >= 45 ? r.u32() : 0;
  const airshows = version >= 45 ? r.u32() : 0;
  let airlinesSeen: number[] | null = null;
  if (version >= 46) {
    airlinesSeen = [];
    for (let n = r.u8(); n > 0; n--) airlinesSeen.push(r.u8());
  }
  const noiseInsulation = version >= 47 ? r.u8() : 0;
  const legacyWideStands = version >= 50 ? r.u8() === 1 : null;
  const theme = version >= 52 ? r.u8() : 0;
  const today = readLedger(r, version);
  const yesterday = readLedger(r, version);
  const allTime = version >= 50 ? readLedger(r, version) : null;
  const offers: Offer[] = [];
  for (let n = r.u32(); n > 0; n--) {
    const offer = {
      id: r.u32(),
      airline: r.u8(),
      cls: r.u8() as AircraftClassId,
      flightsPerDay: r.u8(),
      slotOffset: r.u8(),
      days: r.u8(),
      expiresAt: r.u32(),
      renewal: version >= 5 ? r.u8() : 0,
      slots: [] as number[],
      cargo: 0,
      event: 0,
      rental: 0,
    };
    if (version >= 10) for (let k = r.u8(); k > 0; k--) offer.slots.push(r.u16());
    if (version >= 13) offer.cargo = r.u8();
    if (version >= 22) offer.event = r.u8();
    if (version >= 36) offer.rental = r.u8();
    offers.push(offer);
  }
  const contracts: Contract[] = [];
  for (let n = r.u32(); n > 0; n--) {
    const id = r.u32();
    const airline = r.u8();
    const cls = r.u8() as AircraftClassId;
    const endsAt = r.u32();
    const served = r.u32();
    const cancelled = r.u32();
    const revenue = r.f64();
    const score = version >= 5 ? r.u8() : CONTRACT_INITIAL_SCORE;
    const slots: number[] = [];
    for (let k = r.u8(); k > 0; k--) slots.push(r.u16());
    const strikes = version >= 10 ? r.u8() : 0;
    const cargo = version >= 13 ? r.u8() : 0;
    const event = version >= 22 ? r.u8() : 0;
    const rental = version >= 36 ? r.u8() : 0;
    const report = emptyReport();
    if (version >= 50) for (const k of REPORT_FIELDS) report[k] = r.u32();
    contracts.push({
      id,
      airline,
      cls,
      slots,
      endsAt,
      served,
      cancelled,
      revenue,
      score,
      strikes,
      cargo,
      event,
      rental,
      report,
    });
  }
  const flights: Flight[] = [];
  for (let n = r.u32(); n > 0; n--) {
    const base = {
      id: r.u32(),
      contractId: r.u32(),
      airline: r.u8(),
      cls: r.u8() as AircraftClassId,
      scheduledAt: r.u32(),
      status: r.u8() as FlightStatusId,
      holdReason: r.u8() as HoldReasonId,
      aircraftId: r.u32(),
      standId: r.u32(),
      revenue: r.f64(),
      finishedAt: r.u32(),
    };
    if (version < 4) {
      // Pre-passenger flight: empty, departing at its arrival time.
      flights.push({
        ...base,
        departAt: base.scheduledAt,
        paxIn: 0,
        paxOut: 0,
        paxReady: 0,
        paxCheckedIn: 0,
        paxScreened: 0,
        paxBoarded: 0,
        waitSum: 0,
        offBlockAt: 0,
        gateClosed: 1,
        satisfaction: 0,
        lostArrival: 0,
        lostVehicles: 0,
        lostPassengers: 0,
        lostDeparture: 0,
        lostTechnical: 0,
        cargo: 0,
        lateBy: 0,
        transferIn: 0,
        transferOut: 0,
        event: 0,
        lostWeather: 0,
        park: 0,
        vip: 0,
        paxArrived: 0,
      });
      continue;
    }
    const flight: Flight = {
      ...base,
      departAt: r.u32(),
      paxIn: r.u16(),
      paxOut: r.u16(),
      paxReady: r.u16(),
      paxCheckedIn: 0,
      paxScreened: 0,
      paxBoarded: r.u16(),
      waitSum: r.u32(),
      offBlockAt: r.u32(),
      gateClosed: r.u8(),
      satisfaction: r.u8(),
      lostArrival: 0,
      lostVehicles: 0,
      lostPassengers: 0,
      lostDeparture: 0,
      lostTechnical: 0,
      cargo: 0,
      lateBy: 0,
      transferIn: 0,
      transferOut: 0,
      event: 0,
      lostWeather: 0,
      park: 0,
      vip: 0,
      paxArrived: 0,
    };
    if (version >= 6) {
      flight.lostArrival = r.u16();
      flight.lostVehicles = r.u16();
      flight.lostPassengers = r.u16();
      flight.lostDeparture = r.u16();
    }
    if (version >= 7) flight.lostTechnical = r.u16();
    if (version >= 13) flight.cargo = r.u16();
    if (version >= 15) flight.lateBy = r.u16();
    if (version >= 18) {
      flight.transferIn = r.u16();
      flight.transferOut = r.u16();
    }
    if (version >= 24) flight.event = r.u8();
    if (version >= 26) flight.lostWeather = r.u16();
    if (version >= 35) {
      flight.paxCheckedIn = r.u16();
      flight.paxScreened = r.u16();
    } else {
      // One pooled queue before: everyone it served is through every stage.
      flight.paxCheckedIn = flight.paxReady;
      flight.paxScreened = flight.paxReady;
    }
    // v36: night stops and parking rentals; every flight before was a turnaround.
    if (version >= 36) flight.park = r.u8();
    // v37: passengers at the terminal; before, worked out once the world's time is known.
    flight.paxArrived = version >= 37 ? r.u16() : -1;
    // v44: VIPs aboard; none before.
    flight.vip = version >= 44 ? r.u8() : 0;
    flights.push(flight);
  }
  const aircraft: Aircraft[] = [];
  for (let n = r.u32(); n > 0; n--) {
    const a = {
      id: r.u32(),
      flightId: r.u32(),
      cls: r.u8() as AircraftClassId,
      airline: r.u8(),
      phase: r.u8() as PhaseId,
      runwayId: r.u32(),
      runwayDir: version >= 5 ? r.u8() : 0,
      runwayIndex: r.u8(),
      runwayPos: r.u32(),
      speed: r.u32(),
      pathPos: r.u32(),
      standId: r.u32(),
      timer: r.u32(),
      timerTotal: r.u32(),
      path: [] as number[],
      svcLeft: new Array<number>(SERVICE_COUNT).fill(0),
      svcTotal: new Array<number>(SERVICE_COUNT).fill(0),
      // v3 aircraft at a stand simply finish their turnaround.
      boardState: 2,
      boardMilli: 0,
      fuelFromDepot: 0,
      gse: 0,
      contracted: 0,
      deiceLeft: 0,
      deiceTotal: 0,
      techLeft: 0,
      techTotal: 0,
      gseEta: new Array<number>(GSE_COUNT).fill(0),
      crewEta: 0,
      emergency: 0,
    };
    for (let k = r.u32(); k > 0; k--) a.path.push(r.u32());
    if (version >= 4) {
      for (let i = 0; i < SERVICE_COUNT; i++) {
        a.svcLeft[i] = r.u32();
        a.svcTotal[i] = r.u32();
      }
      a.boardState = r.u8();
      a.boardMilli = r.u32();
      a.fuelFromDepot = r.u8();
    }
    if (version >= 6) {
      a.gse = r.u8();
      a.contracted = r.u8();
    }
    if (version >= 7) {
      a.deiceLeft = r.u32();
      a.deiceTotal = r.u32();
      a.techLeft = r.u32();
      a.techTotal = r.u32();
    }
    if (version >= 10) {
      a.crewEta = r.u32();
      for (let k = 0; k < GSE_COUNT; k++) a.gseEta[k] = r.u32();
    }
    if (version >= 11) a.emergency = r.u8();
    aircraft.push(a);
  }
  return {
    nextOpsId,
    rating,
    windDir,
    windSpeed,
    sky,
    visibility,
    climate,
    grantState,
    grantAmount,
    grantDeadline,
    staffAuto,
    staffHired,
    landingFeePct,
    paxFeePct,
    loans,
    airportName,
    ratings,
    buzz,
    runwayClosures,
    milestones,
    departuresByClass,
    staffNight,
    nightNoise,
    transferPool,
    snowfall,
    blizzardAt,
    blizzardUntil,
    runwaySnow,
    snowJobs,
    legacySnow,
    legacyClosed,
    socialReadyAt,
    promoUntil,
    wagePct,
    strikeUntil,
    skyAhead,
    oilIndex,
    economy,
    marketFlags,
    adminMode,
    dailyGoals,
    vipsPleased,
    airshows,
    // Older worlds: the airlines of their contracts that have flown.
    airlinesSeen:
      airlinesSeen ??
      [...new Set(contracts.filter((c) => c.served > 0).map((c) => c.airline))].sort(
        (a, b) => a - b,
      ),
    noiseInsulation,
    legacyWideStands,
    theme,
    today,
    yesterday,
    allTime,
    offers,
    contracts,
    flights,
    aircraft,
  };
}
